/**
 * MADE-065: KanbanCard
 * Single card in the Kanban board. Accepts a normalized WorkOrder.
 * For ProductionOrder blocked cards, renders BlockedCard instead.
 */

import React, { useState } from 'react';
import {
  Paper, Typography, Stack, Chip, Button,
  Alert, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Build as BuildIcon,
  Print as PrintIcon,
  Block as BlockIcon,
  ReceiptLong as ReceiptLongIcon,
  LocalShipping as LocalShippingIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import { CustomerOrderPackSlip } from './CustomerOrderPackSlip';
import type { WorkOrder, ManufacturingOrder } from '../../types/madebb.types';

const TYPE_COLORS: Record<WorkOrder['type'], string> = {
  manufacturing: '#9c27b0',
  assembly: '#2e7d32',
  production: '#1565c0',
  roll_shade: '#00838f',
};

const TYPE_LABELS: Record<WorkOrder['type'], string> = {
  manufacturing: '3D Print',
  assembly: 'Assembly',
  production: 'Production',
  roll_shade: 'Roll Shade',
};

interface KanbanCardProps {
  workOrder: WorkOrder;
  onAssign?: (wo: WorkOrder) => void;
  onQcPass?: (wo: WorkOrder) => void;
  onQcFail?: (wo: WorkOrder) => void;
  onViewPackSlip?: (wo: WorkOrder) => void;
  onViewDetail: (wo: WorkOrder) => void;
  onAdvance: (wo: WorkOrder, newStatus: WorkOrder['kanbanStatus']) => void;
  onMarkShipped?: (wo: WorkOrder) => void;
  onMarkShippedRollShade?: (wo: WorkOrder) => void;
  onCancel?: (wo: WorkOrder) => void;
  loadingActions: Record<string, boolean>;
}

const NEXT_STAGE_MAP: Partial<Record<WorkOrder['kanbanStatus'], { next: WorkOrder['kanbanStatus']; label: string }>> = {
  // manufacturing and assembly follow this path
  received: { next: 'in_production', label: 'Prod' },
  // in_production is handled by onAssign for manufacturing, so no generic advance button
  // quality_check has its own Pass/Fail buttons
  // pack_and_ship has its own Mark Shipped button

  // roll_shade can use this for advancing
  in_production: { next: 'quality_check', label: 'QC' },
};

export const KanbanCard: React.FC<KanbanCardProps> = ({
  workOrder, onAssign, onQcPass, onQcFail, onViewPackSlip, onViewDetail, onAdvance, onMarkShipped, onMarkShippedRollShade, onCancel, loadingActions,
}) => {
  const isUrgent = workOrder.priority === 'urgent';
  const raw = workOrder._raw as ManufacturingOrder;
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const canCancel = !!onCancel && (workOrder.kanbanStatus === 'received' || workOrder.kanbanStatus === 'blocked');

  // MADE-247: prefer shippingMethod (API alias) over legacy shipOrCollect
  const effectiveShipping = workOrder.shippingMethod ?? workOrder.shipOrCollect;

  const nextStage = NEXT_STAGE_MAP[workOrder.kanbanStatus];
  const canAdvance = !!nextStage;

  // True when pack_slip_data carries a customer order slip (has .shipping field)
  // — in that case we render <CustomerOrderPackSlip> inline on the card rather
  // than opening the drawer, so the portal is a direct child of body for printing.
  const hasCustomerPackSlip =
    workOrder.kanbanStatus === 'pack_and_ship' &&
    !!raw.pack_slip_data &&
    raw.pack_slip_data?.shipping !== undefined &&
    raw.pack_slip_data?.shipping !== null;

  const hasSpecificActionButton =
    (workOrder.type === 'manufacturing' && workOrder.kanbanStatus === 'received' && (!!onAssign || raw.fulfillment_type === 'buy')) ||
    (workOrder.kanbanStatus === 'quality_check' && (
      (workOrder.type === 'manufacturing' && !!onQcPass) ||
      (workOrder.type === 'roll_shade' && !!onAdvance) ||
      !!onQcFail
    )) ||
    (workOrder.kanbanStatus === 'pack_and_ship' && (
      hasCustomerPackSlip ||
      (raw.pack_slip_generated && !!onViewPackSlip) ||
      !!onMarkShipped
    ));


  return (
    <Paper
      elevation={1}
      sx={{
        p: 1.5,
        borderRadius: 2,
        borderLeft: isUrgent ? '4px solid #f44336' : undefined,
        cursor: 'pointer',
        '&:hover': { bgcolor: 'grey.50' },
      }}
      onClick={() => onViewDetail(workOrder)}
      data-testid={`kanban-card-${workOrder.id}`}
    >
      {/* MADE-248: Row 1 — order number always on its own line */}
      <Typography
        variant="subtitle2"
        fontWeight={700}
        noWrap
        mb={0.5}
        data-testid="kanban-card-order-number"
      >
        {workOrder.orderNumber}
      </Typography>

      {/* MADE-248: Row 2 — type chip + disposition + shipping badges */}
      <Stack direction="row" alignItems="center" spacing={0.5} mb={0.5} flexWrap="wrap">
        <Chip
          label={TYPE_LABELS[workOrder.type]}
          size="small"
          sx={{
            fontSize: 10,
            height: 20,
            bgcolor: TYPE_COLORS[workOrder.type],
            color: 'white',
          }}
        />
        {raw.disposition_type === 'to_inventory' && (
          <Chip label="→ Inventory" color="success" size="small" data-testid="disposition-badge" />
        )}
        {/* MADE-238/247: Ship/Collect/DHL chip — reads shippingMethod (preferred) or shipOrCollect */}
        {effectiveShipping === 'ship' && (
          <Chip
            icon={<LocalShippingIcon sx={{ fontSize: 14 }} />}
            label="Ship"
            size="small"
            color="primary"
            data-testid="ship-collect-badge"
            sx={{ height: 20, fontSize: 11 }}
          />
        )}
        {effectiveShipping === 'dhl' && (
          <Chip
            icon={<LocalShippingIcon sx={{ fontSize: 14 }} />}
            label="DHL"
            size="small"
            color="info"
            data-testid="dhl-badge"
            sx={{ height: 20, fontSize: 11 }}
          />
        )}
        {effectiveShipping === 'collect' && (
          <Chip
            icon={<StoreIcon sx={{ fontSize: 14 }} />}
            label="Collect"
            size="small"
            color="success"
            data-testid="ship-collect-badge"
            sx={{ height: 20, fontSize: 11 }}
          />
        )}
      </Stack>

      {/* Description */}
      <Typography variant="body2" noWrap color="text.secondary" mb={0.5}>
        {workOrder.description}
      </Typography>

      {/* Customer */}
      {workOrder.customerName && (
        <Typography variant="caption" color="text.secondary" display="block">
          {workOrder.customerName}
        </Typography>
      )}

      {/* MADE-233: Sibling readiness indicator for multi-item customer orders */}
      {raw.disposition_type === 'to_customer' &&
       (raw.source_order_id || raw.invoice_id) &&
       (raw.sibling_count ?? 0) > 0 && (
        <Typography variant="caption" color="text.secondary" display="block" data-testid="sibling-readiness">
          {(raw.siblings_ready ?? 0) + (['pack_and_ship', 'completed'].includes(workOrder.kanbanStatus ?? '') ? 1 : 0)} of {(raw.sibling_count ?? 0) + 1} items ready
        </Typography>
      )}

      {/* Due date */}
      {workOrder.dueDate && (
        <Typography
          variant="caption"
          display="block"
          color={isUrgent ? 'error' : 'text.secondary'}
          fontWeight={isUrgent ? 700 : 400}
        >
          {isUrgent && '⚠ '}Due: {new Date(workOrder.dueDate).toLocaleDateString('en-BB', { month: 'short', day: 'numeric', year: 'numeric' })}
        </Typography>
      )}

      {/* Assigned machine (manufacturing only) */}
      {workOrder.type === 'manufacturing' && raw.assigned_machine && (
        <Typography variant="caption" display="block" color="text.secondary">
          Machine: {raw.assigned_machine}
        </Typography>
      )}

      {/* QC rejection reason */}
      {workOrder.type === 'manufacturing' && raw.qc_rejection_reason && (
        <Alert severity="error" icon={false} sx={{ mt: 0.5, py: 0 }}>
          <Typography variant="caption">QC rejection: {raw.qc_rejection_reason}</Typography>
        </Alert>
      )}

      {/* Blocked indicator (roll_shade or any blocked state) */}
      {workOrder.kanbanStatus === 'blocked' && (
        <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
          <BlockIcon fontSize="small" color="error" />
          <Typography variant="caption" color="error" fontWeight={700}>
            Blocked on {workOrder.blockedOn === 'procurement' ? 'Procurement' : 'Production'}
          </Typography>
        </Stack>
      )}

      {/* Cancel confirmation dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        data-testid="cancel-confirm-dialog"
        onClick={e => e.stopPropagation()}
      >
        <DialogTitle>Cancel this order?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel order <strong>{workOrder.orderNumber}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} color="inherit">Keep</Button>
          <Button
            onClick={() => { setCancelDialogOpen(false); onCancel!(workOrder); }}
            color="error"
            variant="contained"
            disabled={loadingActions[`cancel-${workOrder.id}`]}
          >
            {loadingActions[`cancel-${workOrder.id}`] ? <CircularProgress size={16} color="inherit" /> : 'Cancel Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action buttons — stop propagation so they don't open the drawer */}
      <Stack direction="row" spacing={1} mt={1} justifyContent="center" flexWrap="nowrap" onClick={e => e.stopPropagation()}>
        {/* --- Generic Advance Button --- */}
        {canAdvance && !hasSpecificActionButton && onAdvance && (
          <Button
            size="small"
            variant="contained"
            disabled={loadingActions[`advance-${workOrder.id}`]}
            startIcon={loadingActions[`advance-${workOrder.id}`] ? <CircularProgress size={12} color="inherit" /> : undefined}
            onClick={() => onAdvance(workOrder, nextStage.next)}
            sx={{ fontSize: 11 }}
            data-testid={`advance-btn-${workOrder.id}`}
          >
            Advance to {nextStage.label}
          </Button>
        )}

        {/* --- Type/Status Specific Buttons --- */}
        {workOrder.type === 'manufacturing' && workOrder.kanbanStatus === 'received' && (
          raw.fulfillment_type === 'buy' ? (
            // MADE-242: buy items skip production — jump straight to Pack & Ship
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={loadingActions[`advance-${workOrder.id}`] ? <CircularProgress size={12} color="inherit" /> : <StoreIcon />}
              onClick={() => onAdvance(workOrder, 'pack_and_ship')}
              disabled={loadingActions[`advance-${workOrder.id}`]}
              sx={{ fontSize: 11 }}
              data-testid={`pick-btn-${workOrder.id}`}
            >
              Mark as Picked
            </Button>
          ) : onAssign ? (
            // produce items — full production flow
            <Button
              size="small"
              variant="contained"
              startIcon={loadingActions[`assign-${workOrder.id}`] ? <CircularProgress size={12} color="inherit" /> : <BuildIcon />}
              onClick={() => onAssign(workOrder)}
              disabled={loadingActions[`assign-${workOrder.id}`]}
              sx={{ fontSize: 11 }}
            >
              Assign & Start
            </Button>
          ) : null
        )}
        {workOrder.kanbanStatus === 'quality_check' && (
          <>
            {/* Manufacturing QC Pass */}
            {workOrder.type === 'manufacturing' && onQcPass && (
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={loadingActions[`qc-pass-${workOrder.id}`] ? <CircularProgress size={12} color="inherit" /> : <CheckCircleIcon />}
                onClick={() => onQcPass(workOrder)}
                disabled={loadingActions[`qc-pass-${workOrder.id}`]}
                sx={{ fontSize: 11 }}
              >
                QC Pass
              </Button>
            )}
            {/* Roll Shade QC Pass (advances to pack & ship) */}
            {workOrder.type === 'roll_shade' && onAdvance && (
               <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={loadingActions[`advance-${workOrder.id}`] ? <CircularProgress size={12} color="inherit" /> : <CheckCircleIcon />}
                onClick={() => onAdvance(workOrder, 'pack_and_ship')}
                disabled={loadingActions[`advance-${workOrder.id}`]}
                sx={{ fontSize: 11 }}
              >
                QC Pass
              </Button>
            )}
            {/* Common QC Fail Button */}
            {onQcFail && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={loadingActions[`qc-fail-${workOrder.id}`] ? <CircularProgress size={12} color="inherit" /> : <CancelIcon />}
                onClick={() => onQcFail(workOrder)}
                disabled={loadingActions[`qc-fail-${workOrder.id}`]}
                sx={{ fontSize: 11 }}
              >
                QC Fail
              </Button>
            )}
          </>
        )}
        {/* Customer order slip: render inline — component shows only a Print Slip
             button on screen; its portal is a direct child of body for correct printing */}
        {hasCustomerPackSlip && (
          <CustomerOrderPackSlip
            packSlipData={raw.pack_slip_data!}
            orderNumber={workOrder.orderNumber}
          />
        )}
        {/* Legacy / disposition slip: open drawer via onViewPackSlip */}
        {!hasCustomerPackSlip && workOrder.kanbanStatus === 'pack_and_ship' && raw.pack_slip_generated && onViewPackSlip && (
          <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={() => onViewPackSlip(workOrder)} sx={{ fontSize: 11 }}>
            Pack Slip
          </Button>
        )}
        {workOrder.kanbanStatus === 'pack_and_ship' && onMarkShipped && (
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={loadingActions[`ship-${workOrder.id}`]}
            startIcon={loadingActions[`ship-${workOrder.id}`] ? <CircularProgress size={12} color="inherit" /> : undefined}
            onClick={() => onMarkShipped(workOrder)}
            sx={{ fontSize: 11 }}
          >
            {raw.ship_or_collect === 'collect' ? '✅ Mark Ready for Collection' : '✅ Mark Shipped'}
          </Button>
        )}
        {/* Cancel button for received/blocked orders */}
        {canCancel && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={loadingActions[`cancel-${workOrder.id}`] ? <CircularProgress size={12} color="inherit" /> : <CancelIcon />}
            onClick={() => setCancelDialogOpen(true)}
            disabled={loadingActions[`cancel-${workOrder.id}`]}
            sx={{ fontSize: 11, ml: 'auto' }}
            data-testid="cancel-order-btn"
          >
            Cancel
          </Button>
        )}
      </Stack>
    </Paper>
  );
};
